import React, { ReactNode, useId } from "react";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

export interface SortOption<TField extends string> {
  field: TField;
  direction: TableSortDirection;
  label: ReactNode;
}

// A field with an arrow instead of a word, from the same icon set as the app's
// other arrows (LinkWithArrow): down for high to low, up for low to high.
// Screen readers get the word.
export const DirectionLabel = ({
  field,
  direction,
  directionText,
}: {
  field: string;
  direction: TableSortDirection;
  directionText: string;
}) => (
  <Box component="span" display="inline-flex" alignItems="center" gap={1}>
    {field}
    {direction === "ASC" ? (
      <ArrowUpwardIcon
        titleAccess={directionText}
        sx={{ fontSize: 16, color: "icon.secondary" }}
      />
    ) : (
      <ArrowDownwardIcon
        titleAccess={directionText}
        sx={{ fontSize: 16, color: "icon.secondary" }}
      />
    )}
  </Box>
);

const optionKey = ({
  field,
  direction,
}: {
  field: string;
  direction: TableSortDirection;
}) => `${field}-${direction}`;

interface Props<TField extends string> {
  label: string;
  options: SortOption<TField>[];
  field: TField;
  direction: TableSortDirection;
  onChange: (field: TField, direction: TableSortDirection) => void;
}

// One choice sets field and direction together.
export const SortSelect = <TField extends string>({
  label,
  options,
  field,
  direction,
  onChange,
}: Props<TField>) => {
  const labelId = useId();

  return (
    <FormControl fullWidth sx={{ "& .MuiMenu-paper": { maxHeight: 300 } }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        size="small"
        value={optionKey({ field, direction })}
        onChange={(event) => {
          const option = options.find(
            (candidate) => optionKey(candidate) === event.target.value
          );
          if (option) onChange(option.field, option.direction);
        }}
      >
        {options.map((option) => (
          <MenuItem key={optionKey(option)} value={optionKey(option)}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
